<template>
  <v-card class="attachment-box">
    <v-card-title class="section-header bg-purple-lighten-5">
      <v-icon size="20" color="purple-darken-1" class="mr-2"
        >mdi-paperclip</v-icon
      >
      <span>Bijlages</span>
      <v-chip
        v-if="attachments.length > 0"
        class="ml-2"
        size="x-small"
        color="purple-lighten-1"
        variant="tonal"
      >
        {{ attachments.length }}
      </v-chip>
      <v-spacer></v-spacer>
      <v-btn
        color="purple-darken-1"
        variant="text"
        size="small"
        class="ml-2"
        @click="addAttachment"
      >
        <v-icon start>mdi-plus</v-icon>
        Toevoegen
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-0">
      <!-- Images section -->
      <div v-if="image_attachments.length > 0" class="attachment-section">
        <div class="d-flex align-center mb-2">
          <v-icon color="purple-darken-1" size="small" class="mr-2"
            >mdi-image</v-icon
          >
          <span class="text-subtitle-2">Afbeeldingen</span>
        </div>
        <v-carousel
          v-model="carouselIndex"
          hide-delimiters
          show-arrows="hover"
          height="220"
          class="attachment-carousel"
        >
          <v-carousel-item
            v-for="(attachment, i) in image_attachments"
            :key="i"
            :src="attachment.thumbnail || attachment.file"
            cover
            @click="viewImage(attachment)"
          >
            <template #placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular
                  indeterminate
                  color="purple-lighten-2"
                ></v-progress-circular>
              </v-row>
            </template>

            <div class="image-overlay d-flex align-center justify-center">
              <v-btn
                icon="mdi-magnify-plus"
                variant="tonal"
                color="white"
                size="small"
                class="mx-1"
                title="Vergroten"
                @click.stop="viewImage(attachment)"
              ></v-btn>
              <v-btn
                icon="mdi-pencil"
                variant="tonal"
                color="white"
                size="small"
                class="mx-1"
                title="Bewerken"
                @click.stop="editAttachment(attachment)"
              ></v-btn>
              <v-btn
                icon="mdi-download"
                variant="tonal"
                color="white"
                size="small"
                class="mx-1"
                title="Downloaden"
                :href="attachment.file_dl"
                target="_blank"
                @click.stop
              ></v-btn>
              <v-btn
                icon="mdi-delete"
                variant="tonal"
                color="error"
                size="small"
                class="mx-1"
                title="Verwijderen"
                @click.stop="confirmDeleteAttachment(attachment)"
              ></v-btn>
            </div>
          </v-carousel-item>
        </v-carousel>

        <!-- Image Lightbox -->
        <ImageLightbox
          v-model="showLightbox"
          :images="image_attachments"
          :initial-index="selectedImageIndex"
          :allow-delete="true"
          @delete="confirmDeleteFromLightbox"
        />
      </div>

      <!-- Empty state for images -->
      <div v-else-if="!loading" class="empty-state">
        <v-icon color="grey-lighten-1" size="large" class="mb-2"
          >mdi-image-outline</v-icon
        >
        <div class="text-body-2 text-medium-emphasis">
          Nog geen foto's toegevoegd
        </div>
        <v-btn
          color="purple-darken-1"
          variant="text"
          size="small"
          class="mt-2"
          @click="addAttachment"
        >
          <v-icon start>mdi-image-plus</v-icon>
          Foto toevoegen
        </v-btn>
      </div>

      <!-- Other files section -->
      <v-divider
        v-if="image_attachments.length > 0 && other_attachments.length > 0"
      ></v-divider>

      <div v-if="other_attachments.length > 0" class="attachment-section">
        <div class="d-flex align-center mb-3">
          <v-icon color="purple-darken-1" size="small" class="mr-2"
            >mdi-file-document</v-icon
          >
          <span class="text-subtitle-2">Documenten</span>
        </div>

        <div class="attachment-files">
          <AttachmentChip
            :attachments="other_attachments"
            @refresh="loadAttachments"
          />
        </div>
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="d-flex justify-center align-center pa-6">
        <v-progress-circular
          indeterminate
          color="purple-lighten-2"
        ></v-progress-circular>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { onMounted, watch, computed, ref } from "vue";
import AttachmentChip from "./AttachmentChip.vue";
import ImageLightbox from "../ui/ImageLightbox.vue";
import { useFormsStore } from "@/stores/formsStore";
import { useAttachmentsStoreRefactor } from "@/stores/attachmentsStoreRefactor";
import { useAlertStore } from "@/stores/alertStore";

export default {
  name: "AttachmentsBox",
  components: {
    AttachmentChip,
    ImageLightbox,
  },
  props: {
    recordType: {
      type: String,
      required: true,
    },
    recordId: {
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const formsStore = useFormsStore();
    const attachmentsStore = useAttachmentsStoreRefactor();
    const alertStore = useAlertStore();
    const showLightbox = ref(false);
    const selectedImage = ref(null);
    const selectedImageIndex = ref(0);
    const carouselIndex = ref(0);

    // Get attachments for this specific record (filtered by name: "attachments" in store)
    const attachments = computed(
      () =>
        attachmentsStore.getAttachmentsForRecord(
          props.recordType,
          props.recordId,
        ) || [],
    );

    const loading = computed(() => attachmentsStore.loading);

    const loadAttachments = async () => {
      if (props.recordId) {
        try {
          console.log(
            "AttachmentsBox: Loading attachments for:",
            props.recordType,
            props.recordId,
          );

          // Get existing attachments and check if they need to be refreshed
          const existingAttachments = attachmentsStore.getAttachmentsForRecord(
            props.recordType,
            props.recordId,
          );
          const shouldRefetch = attachmentsStore.shouldRefetchForRecord(
            props.recordType,
            props.recordId,
          );

          // Only fetch if we don't have attachments or they need to be refreshed
          if (existingAttachments.length === 0 || shouldRefetch) {
            await attachmentsStore.fetchByRecord(
              props.recordType,
              props.recordId,
            );
            console.log(
              "AttachmentsBox: Attachments loaded for record:",
              attachmentsStore.getAttachmentsForRecord(
                props.recordType,
                props.recordId,
              ).length,
            );
          } else {
            console.log(
              `AttachmentsBox: Using ${existingAttachments.length} cached attachments`,
            );
          }
        } catch (error) {
          console.error("AttachmentsBox: Error loading attachments:", error);
        }
      } else {
        console.warn(
          "AttachmentsBox: Cannot load attachments - No recordId provided",
        );
      }
    };

    const addAttachment = () => {
      formsStore.edit({
        type: "image",
        item: {
          can_change_image: true,
          file: "",
          file_dl: "",
          recordId: props.recordId,
          recordType: props.recordType,
          field: "attachments",
        },
        options: {
          filetype: "*",
          recordType: props.recordType,
          recordId: props.recordId,
          field: "attachments",
          onComplete: loadAttachments,
        },
      });
    };

    const editAttachment = (attachment) => {
      formsStore.edit({
        type: "image",
        item: {
          id: attachment.id,
          can_change_image: true,
          file: attachment.file || null,
          file_dl: attachment.file_dl || null,
          recordId: props.recordId,
          recordType: props.recordType,
          field: "attachments",
        },
        options: {
          filetype: "*",
          recordType: props.recordType,
          recordId: props.recordId,
          field: "attachments",
          onComplete: loadAttachments,
        },
      });
    };

    const viewImage = (attachment) => {
      selectedImage.value = attachment;
      // Find the index of the selected image in the array
      selectedImageIndex.value = image_attachments.value.findIndex(
        (img) => img.id === attachment.id,
      );
      if (selectedImageIndex.value === -1) {
        selectedImageIndex.value = 0;
      }
      showLightbox.value = true;
    };

    const deleteAttachment = async (attachment) => {
      try {
        await attachmentsStore.destroy(attachment.id);
        alertStore.success("Bijlage succesvol verwijderd");

        // Refresh attachments after deletion
        await loadAttachments();

        // If we deleted the current carousel item, adjust the index
        if (carouselIndex.value >= image_attachments.value.length) {
          carouselIndex.value = Math.max(0, image_attachments.value.length - 1);
        }
      } catch (error) {
        console.error("Error deleting attachment:", error);
        alertStore.error("Fout bij verwijderen van bijlage");
      }
    };

    const confirmDeleteAttachment = (attachment) => {
      if (
        confirm(
          `Weet je zeker dat je deze bijlage wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`,
        )
      ) {
        deleteAttachment(attachment);
      }
    };

    const confirmDeleteFromLightbox = (imageIndex) => {
      const attachment = image_attachments.value[imageIndex];
      if (attachment) {
        if (
          confirm(
            `Weet je zeker dat je deze bijlage wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`,
          )
        ) {
          deleteAttachment(attachment);
          // Close lightbox if no more images
          if (image_attachments.value.length <= 1) {
            showLightbox.value = false;
          }
        }
      }
    };

    // Load attachments when the component mounts
    onMounted(() => {
      loadAttachments();
    });

    // Watch for changes in recordId and reload if needed
    watch(
      () => props.recordId,
      (newId) => {
        if (newId) {
          loadAttachments();
        }
      },
    );

    const image_attachments = computed(() => {
      return attachments.value?.filter((el) => el.image === true) || [];
    });

    const other_attachments = computed(() => {
      return attachments.value?.filter((el) => el.image === false) || [];
    });

    return {
      attachments,
      loading,
      image_attachments,
      other_attachments,
      addAttachment,
      editAttachment,
      viewImage,
      loadAttachments,
      showLightbox,
      selectedImage,
      selectedImageIndex,
      carouselIndex,
      confirmDeleteAttachment,
      confirmDeleteFromLightbox,
    };
  },
};
</script>

<style scoped>
.attachment-box {
  border-radius: 12px;
  overflow: hidden;
}

.section-header {
  font-size: 0.875rem;
  font-weight: 500;
  padding: 12px 16px;
  min-height: 48px;
  display: flex;
  align-items: center;
}

.attachment-carousel {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 8px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
  opacity: 0;
  transition: opacity 0.2s ease;
}

.v-carousel-item:hover .image-overlay {
  opacity: 1;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
}

.attachment-files {
  display: flex;
  flex-wrap: wrap;
}

.attachment-section {
  padding: 16px;
}

/* Mobile optimizations */
@media (max-width: 600px) {
  .attachment-title {
    padding: 10px 12px;
  }

  .attachment-section {
    padding: 12px;
  }

  .empty-state {
    padding: 16px;
  }

  .attachment-box {
    border-radius: 4px;
  }
}
</style>
